import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Zap, Target, BarChart3, Users, Rocket, Star, ArrowRight, Bot, TrendingUp, Clock, Shield, MessageCircle, ShoppingCart, CreditCard, FileText, Calendar, Image, Video, Mail } from "lucide-react";

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Bot className="h-8 w-8 text-primary" />
              <span className="text-2xl font-bold gradient-text">CloudFly Marketing AI Pro</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-muted-foreground hover:text-primary transition-colors">Características</a>
              <a href="#pricing" className="text-muted-foreground hover:text-primary transition-colors">Precios</a>
              <a href="#testimonials" className="text-muted-foreground hover:text-primary transition-colors">Testimonios</a>
              <Button variant="outline" className="mr-2">Iniciar Sesión</Button>
              <Button className="bg-gradient-to-r from-primary to-purple-600 hover:opacity-90">Prueba Gratis</Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-purple-50 to-green-50 py-20">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">
                🚀 Plataforma Integral de Negocios con IA
              </Badge>
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                La Plataforma Completa
                <span className="gradient-text block">para Hacer Crecer tu Negocio</span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Chatbot multicanal, automatización de ventas, gestión de anuncios, CRM, POS, contabilidad, 
                generación de contenido y mucho más. Todo en una sola plataforma impulsada por IA.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-gradient-to-r from-primary to-purple-600 hover:opacity-90 text-lg px-8 py-6 hover-glow">
                  Comenzar Prueba Gratuita
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 py-6">
                  Ver Demo en Vivo
                </Button>
              </div>
              <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>14 días gratis</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Sin tarjeta de crédito</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Configuración en 5 min</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-purple-600/20 rounded-3xl blur-3xl animate-pulse-slow"></div>
              <img 
                src="/images/ai_dashboard_1.webp" 
                alt="Dashboard de Marketing con IA" 
                className="relative rounded-2xl shadow-2xl hover-glow animate-float"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">500+</div>
              <div className="text-muted-foreground">Empresas Activas</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">300%</div>
              <div className="text-muted-foreground">Aumento Promedio ROI</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">24/7</div>
              <div className="text-muted-foreground">Automatización Activa</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">85%</div>
              <div className="text-muted-foreground">Ahorro de Tiempo</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-primary/10 text-primary border-primary/20 mb-4">
              Características Principales
            </Badge>
            <h2 className="text-4xl font-bold mb-4">Todo lo que Necesitas para Dominar el Marketing Digital</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Nuestro equipo de IA especializado trabaja 24/7 para hacer crecer tu negocio
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="hover-glow border-0 shadow-lg">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-r from-primary to-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <MessageCircle className="h-6 w-6 text-white" />
                </div>
                <CardTitle>Chatbot AI Multicanal 24/7</CardTitle>
                <CardDescription>
                  Atiende clientes en WhatsApp, Facebook, Instagram y TikTok. Cierra ventas automáticamente
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover-glow border-0 shadow-lg">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                  <Target className="h-6 w-6 text-white" />
                </div>
                <CardTitle>Ads Manager Automático</CardTitle>
                <CardDescription>
                  Administra y optimiza tus campañas en Meta Ads y Google Ads automáticamente
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover-glow border-0 shadow-lg">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <CardTitle>CRM + POS Integrado</CardTitle>
                <CardDescription>
                  Gestión completa de clientes, ventas y punto de venta en una sola plataforma
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover-glow border-0 shadow-lg">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mb-4">
                  <CreditCard className="h-6 w-6 text-white" />
                </div>
                <CardTitle>Gestión Empresarial Completa</CardTitle>
                <CardDescription>
                  Contabilidad, nómina, pedidos y facturación electrónica automatizada
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover-glow border-0 shadow-lg">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mb-4">
                  <Image className="h-6 w-6 text-white" />
                </div>
                <CardTitle>Generador de Contenido IA</CardTitle>
                <CardDescription>
                  Crea contenido para redes sociales, anuncios, imágenes y videos automáticamente
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="hover-glow border-0 shadow-lg">
              <CardHeader>
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <CardTitle>Marketing Automático Personalizable</CardTitle>
                <CardDescription>
                  Envíos de marketing automáticos personalizados según el comportamiento del cliente
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-green-100 text-green-700 border-green-200 mb-4">
                Resultados Comprobados
              </Badge>
              <h2 className="text-4xl font-bold mb-6">Transforma tu Negocio con CloudFly</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Ventas 24/7 Multicanal</h3>
                    <p className="text-muted-foreground">Chatbot IA que vende en WhatsApp, Facebook, Instagram y TikTok sin descanso</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Target className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Gestión Publicitaria Automática</h3>
                    <p className="text-muted-foreground">Optimiza Meta Ads y Google Ads automáticamente para maximizar conversiones</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CreditCard className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Gestión Empresarial Integral</h3>
                    <p className="text-muted-foreground">CRM, POS, contabilidad, nómina y facturación electrónica en una plataforma</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Image className="h-4 w-4 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Contenido y Anuncios Automáticos</h3>
                    <p className="text-muted-foreground">Genera contenido, imágenes, videos y campañas de marketing personalizadas</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/images/marketing_team_1.jpeg" 
                alt="Equipo de marketing profesional" 
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-primary/10 text-primary border-primary/20 mb-4">
              Planes y Precios
            </Badge>
            <h2 className="text-4xl font-bold mb-4">Elige el Plan Perfecto para tu Negocio</h2>
            <p className="text-xl text-muted-foreground">
              Comienza gratis y escala según tus necesidades
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Starter Plan */}
            <Card className="border-2 hover-glow">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Starter</CardTitle>
                <div className="text-4xl font-bold text-primary mt-4">€197<span className="text-lg text-muted-foreground">/mes</span></div>
                <CardDescription>Perfecto para emprendedores</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Chatbot IA en 2 canales</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">CRM básico + POS</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Generador de contenido IA</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Facturación electrónica</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Soporte por email</span>
                  </div>
                </div>
                <Button className="w-full mt-6" variant="outline">
                  Comenzar Prueba Gratuita
                </Button>
              </CardContent>
            </Card>

            {/* Professional Plan */}
            <Card className="border-2 border-primary shadow-lg hover-glow relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-primary text-white">Más Popular</Badge>
              </div>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Professional</CardTitle>
                <div className="text-4xl font-bold text-primary mt-4">€397<span className="text-lg text-muted-foreground">/mes</span></div>
                <CardDescription>Para empresas en crecimiento</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Chatbot IA multicanal completo</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Ads Manager automático</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">CRM + POS + Contabilidad</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Generación contenido + anuncios</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Nómina + Pedidos</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Soporte prioritario</span>
                  </div>
                </div>
                <Button className="w-full mt-6 bg-gradient-to-r from-primary to-purple-600">
                  Comenzar Ahora
                </Button>
              </CardContent>
            </Card>

            {/* Enterprise Plan */}
            <Card className="border-2 hover-glow">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Enterprise</CardTitle>
                <div className="text-4xl font-bold text-primary mt-4">€697<span className="text-lg text-muted-foreground">/mes</span></div>
                <CardDescription>Para grandes organizaciones</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Plataforma completa ilimitada</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">IA personalizada + API</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Integraciones personalizadas</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Account manager dedicado</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Soporte 24/7 + Consultoría</span>
                  </div>
                </div>
                <Button className="w-full mt-6" variant="outline">
                  Contactar Ventas
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 mb-4">
              Lo que Dicen Nuestros Clientes
            </Badge>
            <h2 className="text-4xl font-bold mb-4">Resultados Reales de Empresas Reales</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover-glow border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "CloudFly transformó completamente nuestro negocio. El chatbot vende 24/7 en WhatsApp y redes sociales. Aumentamos ventas 250% y automatizamos toda la gestión."
                </p>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-primary to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                    MC
                  </div>
                  <div>
                    <div className="font-semibold">María Carmen López</div>
                    <div className="text-sm text-muted-foreground">CEO, TechStart Solutions</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover-glow border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "El Ads Manager automático optimiza nuestras campañas sin intervención. La facturación electrónica y contabilidad nos ahorra 20 horas semanales. ROI del 400%."
                </p>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-semibold">
                    JR
                  </div>
                  <div>
                    <div className="font-semibold">Javier Rodríguez</div>
                    <div className="text-sm text-muted-foreground">Director Marketing, RetailMax</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover-glow border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "El generador de contenido e imágenes es increíble. Crea anuncios perfectos automáticamente. Engagement subió 180% y el costo por lead bajó 60%. Indispensable."
                </p>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white font-semibold">
                    AS
                  </div>
                  <div>
                    <div className="font-semibold">Ana Sánchez</div>
                    <div className="text-sm text-muted-foreground">CMO, Digital Ventures</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary via-purple-600 to-green-500 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            ¿Listo para Revolucionar tu Marketing?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Únete a más de 500 empresas que ya están dominando el marketing digital con IA
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100 text-lg px-8 py-6">
              Comenzar Prueba Gratuita de 14 Días
              <Rocket className="ml-2 h-5 w-5" />
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8 py-6">
              Agendar Demo Personalizada
            </Button>
          </div>
          <p className="text-sm mt-6 opacity-75">
            Sin compromiso • Sin tarjeta de crédito • Configuración en 5 minutos
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Bot className="h-8 w-8 text-primary" />
                <span className="text-2xl font-bold">CloudFly Marketing AI Pro</span>
              </div>
              <p className="text-gray-400">
                La plataforma integral que combina chatbot multicanal, automatización de ventas, gestión empresarial y generación de contenido con IA.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Producto</h3>
              <div className="space-y-2 text-gray-400">
                <div>Características</div>
                <div>Precios</div>
                <div>Integraciones</div>
                <div>API</div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Empresa</h3>
              <div className="space-y-2 text-gray-400">
                <div>Sobre Nosotros</div>
                <div>Blog</div>
                <div>Carreras</div>
                <div>Contacto</div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Soporte</h3>
              <div className="space-y-2 text-gray-400">
                <div>Centro de Ayuda</div>
                <div>Documentación</div>
                <div>Estado del Sistema</div>
                <div>Seguridad</div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 CloudFly Marketing AI Pro. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
